import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { fromEvent, interval } from 'rxjs';
import { EnvironmentService } from '@/services/environment/environment.service';

const UPDATE_CHECK_INTERVAL_MS = 60_000;

@Injectable({
	providedIn: 'root',
})
export class UpdateService {
	readonly pendingUpdate = signal(false);
	readonly bannerDismissed = signal(false);
	readonly showBanner = computed(() => this.pendingUpdate() && !this.bannerDismissed());

	private readonly document = inject(DOCUMENT);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly destroyRef = inject(DestroyRef);
	private readonly environmentService = inject(EnvironmentService);
	private readonly updates = inject(SwUpdate);
	private started = false;

	start(): void {
		if (this.started || !isPlatformBrowser(this.platformId) || !this.updates.isEnabled) {
			return;
		}

		this.started = true;
		this.init();
	}

	dismissBanner(): void {
		this.bannerDismissed.set(true);
	}

	async applyUpdate(): Promise<void> {
		try {
			const activated = await this.updates.activateUpdate();
			if (activated) {
				window.location.reload();
				return;
			}

			this.logWarning('activateUpdate returned false — new version not activated');
		} catch (error) {
			this.logWarning('activateUpdate failed', error);
		}
	}

	private init(): void {
		this.updates.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
			if (event.type === 'VERSION_READY') {
				this.pendingUpdate.set(true);
				this.bannerDismissed.set(false);
			}
		});

		this.updates.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			window.location.reload();
		});

		fromEvent(this.document, 'visibilitychange')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (this.document.visibilityState === 'visible') {
					this.checkAndRemind();
				}
			});

		interval(UPDATE_CHECK_INTERVAL_MS)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.checkAndRemind());

		this.checkAndRemind();
	}

	private checkAndRemind(): void {
		void this.updates.checkForUpdate().catch((error) => this.logWarning('checkForUpdate failed', error));

		if (this.pendingUpdate()) {
			this.bannerDismissed.set(false);
		}
	}

	private logWarning(message: string, error?: unknown): void {
		if (!this.environmentService.environment.production) {
			console.warn(`[UpdateService] ${message}`, error);
		}
	}
}
